import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ResponsiveContainer,
} from "recharts";
import { humanEUR } from "../blocks/format";

// Finance-grade palette (SP1 approved mockup): actual is the solid accent line, forecast is the
// same "projected" teal used elsewhere in SP1 (StackedTrendChart's AI_FORECAST_COLOR family) but
// dashed, and budget stays the shared red used by every other SP1 chart.
export const ACTUAL_COLOR = "#2b3a67";
export const FORECAST_COLOR = "#0e8a7d";
export const BUDGET_COLOR = "#c0392b";
export const BAND_COLOR = "#0e8a7d";
export const BAND_FILL_OPACITY = 0.12;
const GRID_COLOR = "#e5e7eb";
const AXIS_COLOR = "#6b6b6b";

// firstForecastDate - pure helper: the date label of the first is_forecast point, used to place
// the actual/forecast ReferenceLine divider. Returns null when there is no forecast segment, or
// when every point is a forecast (idx 0 - there's no "actual" side to mark the edge of, so a
// divider would be meaningless). Mirrors StackedTrendChart's firstForecastMonth.
export function firstForecastDate(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  const idx = points.findIndex((p) => p && p.is_forecast);
  if (idx <= 0) return null;
  return points[idx].date;
}

// withBand - pure helper: adds a `band: [lower, upper]` tuple to points that have both bounds,
// the standard Recharts idiom for an area-between-two-lines (a single Area whose dataKey resolves
// to a [min,max] pair per point - see src/views/cost-control/forecast/format.js#toBandSeries,
// the existing precedent this mirrors). Points missing either bound are left unbanded, which
// Recharts (with connectNulls={false}) renders as a gap - exactly what confines the shaded band
// to the forecast region, since history points carry lower/upper=null on the wrong side of the
// actual/forecast boundary.
export function withBand(points) {
  if (!Array.isArray(points)) return [];
  return points.map((p) => (p && p.lower != null && p.upper != null ? { ...p, band: [p.lower, p.upper] } : p));
}

const LEGEND_ITEMS = [
  { key: "actual", label: "Actual", color: ACTUAL_COLOR },
  { key: "forecast", label: "Forecast", color: FORECAST_COLOR },
  { key: "budget", label: "Budget", color: BUDGET_COLOR },
  { key: "band", label: "90% range", color: BAND_COLOR },
];

// StaticLegend - plain HTML swatches, not recharts <Legend>. Recharts' Legend defaults to a
// click-to-toggle-series control, which is dead (and confusing) chrome on a page that only ever
// renders once into a headless-Chrome PDF. A static row communicates the same mapping.
function StaticLegend() {
  return (
    <div
      className="forecast-band-chart-legend"
      role="list"
      aria-label="Chart legend"
      style={{
        display: "flex", gap: "16px", justifyContent: "center", marginTop: "6px",
        fontSize: "9pt", color: AXIS_COLOR, fontFamily: "Georgia, 'Times New Roman', serif",
      }}
    >
      {LEGEND_ITEMS.map((item) => (
        <span key={item.key} role="listitem" style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <span
            aria-hidden="true"
            style={{ width: "10px", height: "10px", borderRadius: "2px", background: item.color, display: "inline-block" }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

/**
 * ForecastBandChart - print-static actual-vs-forecast trend with a shaded 90% confidence band,
 * for the SP1 board report (Task-5 GET /figures/forecast-band -> { forecast_band: { points:
 * [{ date, actual, forecast, lower, upper, budget, is_forecast }] } }).
 *
 * actual/forecast/lower/upper/budget are each nullable - nil on the wrong side of the
 * actual/forecast boundary (e.g. a history row has forecast/lower/upper=null, a forecast row has
 * actual=null). `connectNulls={false}` on every series is therefore load-bearing: it stops the
 * actual line at the boundary instead of drawing a phantom flat segment into the forecast region,
 * and (via withBand) confines the shaded band Area to only the forecast points that carry both
 * bounds.
 *
 * Band idiom: a single Area with a `band: [lower, upper]` tuple dataKey (see withBand above) -
 * the same idiom already used by src/views/cost-control/forecast/ForecastChart.jsx, chosen over
 * the two-stacked-Areas alternative because it needs no extra "base" series bookkeeping.
 *
 * Print-static: no Tooltip, no interactive Legend, no hover/zoom - this renders once into a
 * headless-Chrome PDF, where none of that chrome would ever be seen or used. A static legend row
 * stands in for the interactive one.
 */
export default function ForecastBandChart({ points = [] }) {
  if (!Array.isArray(points) || points.length === 0) {
    return (
      <div
        className="forecast-band-chart-empty"
        style={{ fontSize: "9pt", color: AXIS_COLOR, fontStyle: "italic", padding: "8px 0" }}
      >
        No forecast data available.
      </div>
    );
  }

  const banded = withBand(points);
  const divider = firstForecastDate(points);

  return (
    <div className="forecast-band-chart" style={{ width: "100%" }}>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={banded} margin={{ top: 16, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis
              dataKey="date"
              stroke={AXIS_COLOR}
              tick={{ fontSize: 9, fill: AXIS_COLOR }}
              tickLine={false}
              axisLine={{ stroke: GRID_COLOR }}
            />
            <YAxis
              stroke={AXIS_COLOR}
              tick={{ fontSize: 9, fill: AXIS_COLOR }}
              tickFormatter={(v) => humanEUR(v)}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            {divider != null && (
              <ReferenceLine
                x={divider}
                stroke={AXIS_COLOR}
                strokeDasharray="3 3"
                label={{ value: "forecast →", position: "top", fontSize: 8, fill: AXIS_COLOR }}
              />
            )}
            {/* confidence band - rendered first so the actual/forecast/budget lines paint on top */}
            <Area
              type="monotone"
              dataKey="band"
              name="90% range"
              stroke="none"
              fill={BAND_COLOR}
              fillOpacity={BAND_FILL_OPACITY}
              isAnimationActive={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="budget"
              name="Budget"
              stroke={BUDGET_COLOR}
              strokeWidth={1}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            {/* No connectNulls on actual/forecast: each is null on the other's side of the
                boundary, and bridging across it would draw a misleading phantom segment. */}
            <Line
              type="monotone"
              dataKey="actual"
              name="Actual"
              stroke={ACTUAL_COLOR}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="forecast"
              name="Forecast"
              stroke={FORECAST_COLOR}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <StaticLegend />
    </div>
  );
}

ForecastBandChart.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      actual: PropTypes.number,
      forecast: PropTypes.number,
      lower: PropTypes.number,
      upper: PropTypes.number,
      budget: PropTypes.number,
      is_forecast: PropTypes.bool,
    })
  ),
};
